import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Paper,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MatchTimeScoreRecordDto from '../models/dtos/MatchTimeScoreRecordDto';
import UpdateMatchScoreRecordsRequest from '../models/dtos/UpdateMatchScoreRecordsRequest';
import { getMatchScoreRecords, updateMatchScoreRecords } from '../Api';

interface EditScoreRecordsModalProps {
    open: boolean;
    matchId: string | null;
    onClose: () => void;
}

const EditScoreRecordsModal: React.FC<EditScoreRecordsModalProps> = ({ 
    open, 
    matchId,
    onClose
}) => {
    const [scoreRecords, setScoreRecords] = useState<MatchTimeScoreRecordDto[]>([]);
    const [originalRecords, setOriginalRecords] = useState<MatchTimeScoreRecordDto[]>([]);
    const [newlyAddedRows, setNewlyAddedRows] = useState<Set<number>>(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        if (open && matchId) {
            loadScoreRecords();
        }
    }, [open, matchId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadScoreRecords = async () => {
        if (!matchId) return;
        
        setIsLoading(true);
        try {
            const records = await getMatchScoreRecords(matchId);
            if (records) {
                // Sort by minute to ensure proper order
                const sortedRecords = records.sort((a, b) => a.minute - b.minute);
                setScoreRecords(sortedRecords);
                setOriginalRecords([...sortedRecords]);
                setNewlyAddedRows(new Set()); // Reset newly added rows
            } else {
                setScoreRecords([]);
                setOriginalRecords([]);
                setNewlyAddedRows(new Set());
            }
        } catch (error) {
            console.error('Failed to load score records:', error);
            setErrorMessage('Failed to load score records');
            setScoreRecords([]);
            setOriginalRecords([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleScoreChange = (index: number, field: 'scoreHome' | 'scoreAway', value: string) => {
        const newRecords = [...scoreRecords];
        const numValue = parseInt(value) || 0;
        newRecords[index] = { ...newRecords[index], [field]: numValue };
        setScoreRecords(newRecords);
    };

    const handleDeleteRow = (index: number) => {
        const newRecords = [...scoreRecords];
        newRecords.splice(index, 1);
        
        // Update minutes for all remaining rows - each row should have minute = index + 1
        const updatedRecords = newRecords.map((record, idx) => ({
            ...record,
            minute: idx + 1
        }));
        
        // Update newly added rows tracking - shift indices down for rows after deleted index
        const newNewlyAddedRows = new Set<number>();
        newlyAddedRows.forEach(rowIndex => {
            if (rowIndex < index) {
                // Keep rows before deleted row unchanged
                newNewlyAddedRows.add(rowIndex);
            } else if (rowIndex > index) {
                // Shift rows after deleted row down by 1
                newNewlyAddedRows.add(rowIndex - 1);
            }
            // Skip the deleted row (rowIndex === index)
        });
        
        setScoreRecords(updatedRecords);
        setNewlyAddedRows(newNewlyAddedRows);
    };

    const handleAddRowBelow = (index: number) => {
        const newRecords = [...scoreRecords];
        let newRecord: MatchTimeScoreRecordDto;
        
        if (index === -1) {
            // Adding first record to empty list - use default values
            newRecord = {
                minute: 1,
                scoreHome: 0,
                scoreAway: 0
            };
        } else {
            // Copy values from the clicked row
            const sourceRecord = scoreRecords[index];
            newRecord = {
                minute: index + 2, // Will be updated below
                scoreHome: sourceRecord.scoreHome,
                scoreAway: sourceRecord.scoreAway
            };
        }
        
        let insertIndex: number;
        if (index === -1) {
            // Adding first record to empty list
            newRecords.push(newRecord);
            insertIndex = 0;
        } else {
            // Insert the new row at index + 1
            insertIndex = index + 1;
            newRecords.splice(insertIndex, 0, newRecord);
        }
        
        // Update minutes for all rows - each row should have minute = index + 1
        const updatedRecords = newRecords.map((record, idx) => ({
            ...record,
            minute: idx + 1
        }));
        
        // Update newly added rows tracking - shift indices up for rows after insertion point
        const newNewlyAddedRows = new Set<number>();
        newlyAddedRows.forEach(rowIndex => {
            if (rowIndex < insertIndex) {
                // Keep rows before insertion point unchanged
                newNewlyAddedRows.add(rowIndex);
            } else {
                // Shift rows at and after insertion point up by 1
                newNewlyAddedRows.add(rowIndex + 1);
            }
        });
        // Add the newly inserted row
        newNewlyAddedRows.add(insertIndex);
        
        setScoreRecords(updatedRecords);
        setNewlyAddedRows(newNewlyAddedRows);
    };

    const handleSave = async () => {
        if (!matchId) return;
        
        setIsSaving(true);
        try {
            const updateRequest: UpdateMatchScoreRecordsRequest = {
                scoreRecords: scoreRecords
            };
            
            const updatedRecords = await updateMatchScoreRecords(matchId, updateRequest);
            if (updatedRecords) {
                setSuccessMessage('Score records updated successfully');
                setOriginalRecords([...scoreRecords]);
                setNewlyAddedRows(new Set()); // Reset newly added rows after successful save
            } else {
                setErrorMessage('Failed to update score records');
            }
        } catch (error) {
            console.error('Failed to save score records:', error);
            setErrorMessage('Failed to update score records');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset to original data
        setScoreRecords([...originalRecords]);
        setNewlyAddedRows(new Set()); // Reset newly added rows
        setErrorMessage(null);
        setSuccessMessage(null);
        onClose();
    };

    const hasChanges = () => {
        if (scoreRecords.length !== originalRecords.length) return true;
        
        return scoreRecords.some((record, index) => {
            const original = originalRecords[index];
            return !original || 
                   record.minute !== original.minute ||
                   record.scoreHome !== original.scoreHome ||
                   record.scoreAway !== original.scoreAway;
        });
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={handleCancel}
                maxWidth="md" 
                fullWidth
                PaperProps={{ style: { minHeight: '500px' } }}
            >
                <DialogTitle>
                    <Typography variant="h6">Edit Score Records</Typography>
                    {scoreRecords.length > 0 && (
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 0.5 }}>
                            Final score: {scoreRecords[scoreRecords.length - 1].scoreHome}-{scoreRecords[scoreRecords.length - 1].scoreAway}
                        </Typography>
                    )}
                </DialogTitle>
                
                <DialogContent dividers>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box>
                            <TableContainer component={Paper} sx={{ maxHeight: 400, marginBottom: 2 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Minute</TableCell>
                                            <TableCell>Score Home</TableCell>
                                            <TableCell>Score Away</TableCell>
                                            <TableCell width="120px">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {scoreRecords.map((record, index) => (
                                            <TableRow 
                                                key={index}
                                                sx={{
                                                    backgroundColor: newlyAddedRows.has(index) 
                                                        ? 'rgba(33, 150, 243, 0.08)' // Light blue for newly added rows
                                                        : 'transparent'
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography>{record.minute}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        type="number"
                                                        value={record.scoreHome}
                                                        onChange={(e) => handleScoreChange(index, 'scoreHome', e.target.value)}
                                                        size="small"
                                                        inputProps={{ min: 0 }}
                                                        sx={{ width: '80px' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        type="number"
                                                        value={record.scoreAway}
                                                        onChange={(e) => handleScoreChange(index, 'scoreAway', e.target.value)}
                                                        size="small"
                                                        inputProps={{ min: 0 }}
                                                        sx={{ width: '80px' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" gap={0.5}>
                                                        <IconButton
                                                            onClick={() => handleAddRowBelow(index)}
                                                            color="primary"
                                                            size="small"
                                                        >
                                                            <AddIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => handleDeleteRow(index)}
                                                            color="error"
                                                            size="small"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {scoreRecords.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={2}>
                                                        <Typography color="textSecondary">
                                                            No score records found.
                                                        </Typography>
                                                        <Button
                                                            startIcon={<AddIcon />}
                                                            onClick={() => handleAddRowBelow(-1)}
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                        >
                                                            Add First Record
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions>
                    <Button 
                        onClick={handleCancel} 
                        disabled={isSaving}
                        color="inherit"
                    >
                        Close
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        variant="contained" 
                        color="primary"
                        disabled={isSaving || isLoading || !hasChanges()}
                    >
                        {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!errorMessage}
                autoHideDuration={6000}
                onClose={() => setErrorMessage(null)}
            >
                <Alert severity="error" onClose={() => setErrorMessage(null)}>
                    {errorMessage}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!successMessage}
                autoHideDuration={3000}
                onClose={() => setSuccessMessage(null)}
            >
                <Alert severity="success" onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EditScoreRecordsModal;